import React, { useState, useEffect, useRef } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import FormHandler from "../../Common/Form/FormHandler/FormHandler";
import MultiSelectWrapper from "../../Common/Form/MultiSelect/MultiSelect";
import { toast, ToastContainer } from "react-toastify";
import {
  
  createMasterSOPURL,
  updateMasterSOPURL,
  getTaskConfigsURL,
  getTaskListURLById,
  getThemeURL,
  
  getMatrixResources,
  getSOPRsrcDtlBySOPId,
} from "../../../../Services/EndPoints";
import {
  getData,
  postDataWithBody,
  putDataWithBody,
} from "../../../../Services/Services";
import {
  sopformschema,
  SoPFormConfig,
} from "../../../../JsonData/CreateTaskConfig";
import { Button } from "react-bootstrap";
import "./SOPForm.css";
import { useTranslation } from "react-i18next";
import { LayoutState } from "../../../../Context/LayoutContext/LayoutContext";


const SOPForm = () => {
  // State initialization
  const [dataLoadComplete, setDataLoadComplete] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [SoPConfig, setSoPConfig] = useState(SoPFormConfig);
  const [allTasks, setAllTasks] = useState([]);
  const [configData, setConfigData] = useState(null);
  const [stateId, setStateId] = useState(null);
  const [allFetchedResources, setAllFetchedResources] = useState([]);
console.log("data",dataLoadComplete)
  // Use refs to track initialization state and prevent unnecessary re-renders
  const initializationComplete = useRef(false);
  const initialFormSetComplete = useRef(false);
  const editResourcesRef = useRef([]);

  const { state } = useLocation();
  const edit_data = state?.data;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const { collapsed } = LayoutState();
  const [isSelectedResourcesExpanded, setIsSelectedResourcesExpanded] = useState(true);
console.log("set",setActiveTab)
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(sopformschema[activeTab]),
    mode: "all",
    defaultValues: {
      resource_items: []
    }
  });

  const { fields: resourceFields, append: appendResource, remove: removeResource } = useFieldArray({
    control,
    name: "resource_items"
  });

  // const watchedResourceItems = useWatch({
  //   control,
  //   name: "resource_items",
  //   defaultValue: []
  // });

  const selectedIncidentId = watch("incidentId");
  const selectedResource = watch("sop_resource");
  const selectedSeverity = watch("severity");
  const selectedTask = watch("sop_task");

  // Load initial data - Get all configurations first
  useEffect(() => {
    const fetchConfigurations = async () => {
      try {
        console.log("Fetching configurations");
        const configRes = await getTaskConfigs();
        setConfigData(configRes);
        initializationComplete.current = true;
      } catch (error) {
        console.error("Error fetching configurations:", error);
        toast.error("Failed to load configuration data", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
      }
    };

    fetchConfigurations();
  }, []);

  // Handle edit mode setup AFTER configurations are loaded
  useEffect(() => {
    // Only proceed if config data is loaded and form hasn't been set up yet
    if (!configData || initialFormSetComplete.current || !initializationComplete.current) return;

    const setupEditData = async () => {
      try {
        console.log("Setting up edit data");

        if (edit_data) {
          console.log("Processing edit data:", edit_data);

          // Find the corresponding incident ID
          let incidentIdToSet = "";
          if (edit_data.incident_type) {
            const incidentConfig = configData.find(
              config =>
                config.category_name === "INCDNT_CAT" &&
                config.value === edit_data.incident_type
            );

            incidentIdToSet = incidentConfig ? incidentConfig.id.toString() : "";
            console.log("Found incident ID:", incidentIdToSet, "for incident type:", edit_data.incident_type);
          }

          // Construct resource_items for the field array
          let detailedResources = edit_data.resources || [];

          // Fetch detailed resources if missing in edit_data
          if (detailedResources.length === 0 && edit_data.id) {
            try {
              console.log("Fetching detailed resources for SOP ID:", edit_data.id);
              const resDetails = await getData(`${getSOPRsrcDtlBySOPId}${edit_data.id}`);
              if (Array.isArray(resDetails)) {
                detailedResources = resDetails;
              }
            } catch (err) {
              console.error("Error fetching SOP resources:", err);
            }
          }

          const resource_items = [];
          if (detailedResources && Array.isArray(detailedResources)) {
            detailedResources.forEach(res => {
              resource_items.push({
                resource_id: (res.resource_id || res.id)?.toString(),
                severityId: res.severityId || res.severity_id || null,
                name: res.resource_name || res.name || "NA",
                available_quantity: res.available_quantity || 0,
                quantity: res.quantity || res.required_quantity || 1,
                is_automatic: res.is_automatic === true || res.is_automatic === 1 || res.is_automatic === "true" || res.isAutomatic === true,
                is_manual: res.is_manual === true || res.is_manual === 1 || res.is_manual === "true" || res.isManual === true,
                cum_available_quantity: res.cum_available_quantity ?? 0,
                cum_damaged_quantity: res.cum_damaged_quantity ?? 0,
                cum_deployed_quantity: res.cum_deployed_quantity ?? 0,
                cum_total_quantity: res.cum_total_quantity ?? 0
              });
            });
          }

          // Set form values
          const formData = {
            id: edit_data.id,
            name: edit_data.name,
            description: edit_data.description,
            incidentId: incidentIdToSet,
            severity: edit_data.severity || "",
            execution_type: edit_data.execution_type || "",
            resource_items: resource_items
          };

          // Store edit resource data so checkbox values survive matrix re-sync
          editResourcesRef.current = resource_items;

          console.log("Resetting form with data:", formData);
          reset(formData);

          // Incident Category / Severity drive which resources the matrix
          // sync rebuilds the table with — changing them mid-edit is what
          // wipes the resource table's quantities, so lock them once editing
          // an existing SOP.
          setSoPConfig(prevConfig => {
            const updatedConfig = JSON.parse(JSON.stringify(prevConfig));
            const editFields = updatedConfig[0]?.fields || [];
            const incidentField = editFields.find(f => f.name === "incidentId");
            const severityField = editFields.find(f => f.name === "severity");
            if (incidentField) incidentField.disabled = true;
            if (severityField) severityField.disabled = true;
            return updatedConfig;
          });

          // Also handle sop_resource (the MultiSelect)
          const resourceOptions = resource_items.map(r => ({
            label: r.name,
            value: r.resource_id
          }));
          setValue("sop_resource", resourceOptions);

          // Fetch tasks for the selected incident
          if (incidentIdToSet) {
            console.log("Fetching tasks for initial incident:", incidentIdToSet);
            const taskList = await fetchTaskListData(incidentIdToSet);
            setAllTasks(taskList);
            updateTaskOptions(taskList);

            // Set selected tasks if available with a slight delay to ensure options are updated
            if (edit_data.task_id_list && edit_data.task_list) {
              setTimeout(() => {
                populateAttachedTasks(edit_data);
              }, 100);
            }
          }

          // Comma-separated list fallback (only if resources array was missing)
          if (!edit_data.resources && edit_data.resource_id_list && edit_data.resource_list) {
            setTimeout(() => {
              populateAttachedResources(edit_data);
            }, 100);
          }
        }

        initialFormSetComplete.current = true;
        setDataLoadComplete(true);
      } catch (error) {
        console.error("Error setting up edit data:", error);
      }
    };

    setupEditData();
     // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [configData, edit_data]);

  // Update resource options based on Matrix (Category + Severity)
  useEffect(() => {
    // Skip if initialization is not complete or selection missing
    if (!initializationComplete.current || !selectedIncidentId || !selectedSeverity) {
      if (!selectedIncidentId || !selectedSeverity) {
        setAllFetchedResources([]);
        setValue("sop_resource", []);
        setSoPConfig(prev => {
          const updated = JSON.parse(JSON.stringify(prev));
          if (updated[0]?.fields?.[5]) updated[0].fields[5].options = [];
          return updated;
        });
      }
      return;
    }

    const syncResourcesWithMatrix = async () => {
      try {
        console.log("=== Matrix Resource Sync Start ===");

        // Clear existing table and multiselect BEFORE fetching new data.
        // This is critical: if severity changes but resources share the same IDs,
        // the sync effect won't update existing rows (it only adds/removes by ID).
        // Clearing first ensures rows are re-added fresh with correct quantities.
        const currentItems = getValues("resource_items") || [];
        for (let i = currentItems.length - 1; i >= 0; i--) {
          removeResource(i);
        }
        setValue("sop_resource", []);
        setAllFetchedResources([]);

        // Resolve hazardType label
        const catOptions = SoPConfig[0]?.fields?.[0]?.options || [];
        const selectedCat = catOptions.find(opt => opt.value.toString() === selectedIncidentId.toString());
        const hazardType = selectedCat ? selectedCat.label : "";

        if (hazardType) {
          const url = `${getMatrixResources}?hazardType=${encodeURIComponent(hazardType)}&severityLevel=${encodeURIComponent(selectedSeverity)}&stateId=${stateId}`;
          console.log("Fetching matrix resources from:", url);
          const res = await getData(url);

          if (Array.isArray(res)) {
            console.log("Matrix resources received:", res);

            // Map to the format used by the form
            const resources = res.map(item => ({
              id: item.resourceId,
              severityId: item.id,
              name: item.resourceName,
              agency_name: "",
              available_quantity: item.availableQuantity,
              required_quantity: item.requiredQuantity,
              cum_available_quantity: item.cumAvailableQuantity ?? 0,
              cum_damaged_quantity: item.cumDamagedQuantity ?? 0,
              cum_deployed_quantity: item.cumDeployedQuantity ?? 0,
              cum_total_quantity: item.cumTotalQuantity ?? 0,
            }));

            setAllFetchedResources(resources);

            const resourceOptions = resources.map(r => ({
              label: r.name,
              value: r.id.toString()
            }));

            // Update MultiSelect values
            setValue("sop_resource", resourceOptions);

            // Update Config options (even if disabled)
            setSoPConfig(prevConfig => {
              const updatedConfig = JSON.parse(JSON.stringify(prevConfig));
              if (updatedConfig[0]?.fields?.[5]) {
                updatedConfig[0].fields[5].options = resourceOptions;
              }
              return updatedConfig;
            });
          }
        }
      } catch (error) {
        console.error("Error in matrix resource sync:", error);
      } finally {
        console.log("=== Matrix Resource Sync End ===");
      }
    };

    syncResourcesWithMatrix();
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIncidentId, selectedSeverity, SoPConfig[0]?.fields?.[0]?.options?.length]);

  // Synchronize MultiSelect (sop_resource) tags with the Resource Table (resource_items)
  useEffect(() => {
    const currentSelectedResources = Array.isArray(selectedResource) ? selectedResource : [];
    const currentSelectedIds = currentSelectedResources.map(r => r.value.toString());
    const currentTableIds = resourceFields.map(f => f.resource_id.toString());

    // If selection is empty, clear the table completely
    if (currentSelectedResources.length === 0) {
      if (resourceFields.length > 0) {
        [...resourceFields].reverse().forEach((_, i) => removeResource(resourceFields.length - 1 - i));
      }
      return;
    }

    if (!allFetchedResources.length) return;

    // 1. Add missing items from selection to table
    currentSelectedResources.forEach(sel => {
      const selId = sel.value.toString();
      if (!currentTableIds.includes(selId)) {
        const resourceData = allFetchedResources.find(r => r.id.toString() === selId);
        if (resourceData) {
          const editResource = editResourcesRef.current.find(r => r.resource_id?.toString() === selId);
          appendResource({
            resource_id: selId,
            severityId: resourceData.severityId,
            name: resourceData.name,
            agency_name: resourceData.agency_name,
            available_quantity: resourceData.available_quantity,
            quantity: editResource ? editResource.quantity : (resourceData.required_quantity || 1),
            is_automatic: editResource ? editResource.is_automatic : false,
            is_manual: editResource ? editResource.is_manual : false,
            // Prefer the value already saved for this resource in edit mode;
            // otherwise use the cumulative stats the matrix-resources API returns.
            cum_available_quantity: editResource ? editResource.cum_available_quantity : (resourceData.cum_available_quantity ?? 0),
            cum_damaged_quantity: editResource ? editResource.cum_damaged_quantity : (resourceData.cum_damaged_quantity ?? 0),
            cum_deployed_quantity: editResource ? editResource.cum_deployed_quantity : (resourceData.cum_deployed_quantity ?? 0),
            cum_total_quantity: editResource ? editResource.cum_total_quantity : (resourceData.cum_total_quantity ?? 0)
          });
        }
      }
    });

    // 2. Remove items from table that are no longer in selection
    const indicesToRemove = [];
    resourceFields.forEach((field, index) => {
      if (!currentSelectedIds.includes(field.resource_id.toString())) {
        indicesToRemove.push(index);
      }
    });

    // Remove from highest index to lowest to maintain correct relative indices
    if (indicesToRemove.length > 0) {
      indicesToRemove.sort((a, b) => b - a).forEach(idx => removeResource(idx));
    }
  }, [selectedResource, allFetchedResources, resourceFields, appendResource, removeResource, setValue]);

  // Sync available quantities when list is fetched (important for Edit Mode)
  useEffect(() => {
    if (allFetchedResources.length > 0 && resourceFields.length > 0) {
      resourceFields.forEach((field, index) => {
        const resourceData = allFetchedResources.find(r => r.id.toString() === field.resource_id.toString());
        if (resourceData && field.available_quantity !== resourceData.available_quantity) {
          setValue(`resource_items.${index}.available_quantity`, resourceData.available_quantity);
        }
      });
    }
  }, [allFetchedResources, resourceFields, setValue]);

  // Function to remove from both table and multi-select tags
  // const handleRemoveFromTable = (index) => {
  //   const fieldToRemove = resourceFields[index];
  //   const resourceId = fieldToRemove.resource_id;

  //   // Remove from MultiSelect tags - this will automatically trigger the useEffect to remove from table
  //   const currentSelection = getValues("sop_resource") || [];
  //   const newSelection = currentSelection.filter(item => item.value.toString() !== resourceId.toString());
  //   setValue("sop_resource", newSelection);
  // };

  function populateAttachedResources(data) {
    try {
      console.log("Populating resources from data:", data);

      if (data.resources && Array.isArray(data.resources) && data.resources.length > 0) {
        // High-priority: Use the detailed resources list from the API (includes Auto/Manual flags)
        data.resources.forEach(res => {
          const resource_id = (res.resource_id || res.id)?.toString();
          if (!resource_id) return;

          const currentFields = getValues("resource_items") || [];
          const existingIdx = currentFields.findIndex(f => f.resource_id.toString() === resource_id);

          const is_automatic = res.is_automatic === true || res.is_automatic === 1 || res.is_automatic === "true";
          const is_manual = res.is_manual === true || res.is_manual === 1 || res.is_manual === "true";
          const quantity = res.quantity || res.required_quantity || 1;

          if (existingIdx === -1) {
            appendResource({
              resource_id: resource_id,
              severityId: res.severityId || res.severity_id || null,
              name: res.resource_name || res.name || "NA",
              available_quantity: res.available_quantity || 0,
              quantity: quantity,
              is_automatic: is_automatic,
              is_manual: is_manual
            });
          } else {
            // Update existing row (might have been added by sync effect)
            setValue(`resource_items.${existingIdx}.is_automatic`, is_automatic);
            setValue(`resource_items.${existingIdx}.is_manual`, is_manual);
            setValue(`resource_items.${existingIdx}.quantity`, quantity);
          }
        });
      } else if (data.resource_id_list && data.resource_list) {
        // Fallback: Use labels/ids from lists
        const resourceIds = data.resource_id_list.split(",");
        const resourceNames = data.resource_list.split(",");
        const resourceQuantities = data.resource_quantity ? data.resource_quantity.toString().split(",") : [];

        resourceIds.forEach((id, index) => {
          const resource_id = id.trim();
          const currentFields = getValues("resource_items") || [];
          if (!currentFields.some(f => f.resource_id.toString() === resource_id)) {
            appendResource({
              resource_id: resource_id,
              name: (resourceNames[index] || resource_id).trim(),
              available_quantity: resourceQuantities[index] || 0,
              quantity: resourceQuantities[index] || 1,
              is_automatic: false,
              is_manual: false
            });
          }
        });
      }
    } catch (error) {
      console.error("Error populating resources:", error);
    }
  }

  // Handle incident ID changes
  useEffect(() => {
    // Skip if initialization is not complete
    if (!initializationComplete.current) return;

    // Skip if no incident ID selected or in the middle of initial form setup
    if (!selectedIncidentId || !initialFormSetComplete.current) return;

    const handleIncidentChange = async () => {
      try {
        console.log("Incident changed to:", selectedIncidentId);

        // Clear current tasks selection
        setValue("sop_task", []);

        // Fetch tasks for the selected incident
        const taskList = await fetchTaskListData(selectedIncidentId);
        setAllTasks(taskList);
        updateTaskOptions(taskList);
      } catch (error) {
        console.error("Error handling incident change:", error);
      }
    };

    handleIncidentChange();
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIncidentId]);


  // Fetch task configuration data
  async function getTaskConfigs() {
    try {
      const userProfile = await getData(getThemeURL);
      const state_id = userProfile[0]?.state_id;
      setStateId(state_id);
      const res = await postDataWithBody(
        `${getTaskConfigsURL}/${state_id}`,
        ["INCDNT_CAT", "TSK_STAT", "TSK_TYP"],
        {}
      );

      // Process configuration data
      const config_type = {};
      res.forEach(config => {
        if (!config_type[config.category_name]) {
          config_type[config.category_name] = [];
        }

        config_type[config.category_name].push({
          label: config.value,
          value: config.id,
        });
      });

      // Update SoPConfig with incident categories and priority (severity)
      setSoPConfig(prevConfig => {
        const updatedConfig = JSON.parse(JSON.stringify(prevConfig));
        if (updatedConfig[0]?.fields) {
          const fields = updatedConfig[0].fields;

          // Update incident field options at index 0
          if (fields[0]) {
            fields[0].options = config_type["INCDNT_CAT"] || [];
          }

        }
        return updatedConfig;
      });

      console.log("Updated config with incident categories and severity");

      return res;
    } catch (error) {
      console.error("Error fetching task configs:", error);
      toast.error("Failed to fetch configuration data", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      throw error;
    }
  }

  function updateTaskOptions(tasks = allTasks) {
    try {
      const taskOptions = tasks.map(task => ({
        label: task.task_name,
        value: task.id.toString(),
      }));
      setSoPConfig(prevConfig => {
        const updatedConfig = JSON.parse(JSON.stringify(prevConfig));
        const taskFieldIdx = updatedConfig[0]?.fields?.findIndex(f => f.name === "sop_task");
        if (taskFieldIdx >= 0) {
          updatedConfig[0].fields[taskFieldIdx].options = taskOptions;
        }
        return updatedConfig;
      });
    } catch (error) {
      console.error("Error updating task options:", error);
    }
  }

  // Fetch task list data based on incident ID
  async function fetchTaskListData(incident_id) {
    try {
      if (!incident_id) return [];
      const userProfile = await getData(getThemeURL);
      const state_id = userProfile[0]?.state_id;
      // Get tasks for the selected incident
      const taskListUrl = getTaskListURLById.replace("ID", incident_id).replace("STATEID", state_id);
      console.log("Fetching tasks from:", taskListUrl);
      const result = await getData(taskListUrl);
      console.log("Received tasks:", result.length);
      return result;
    } catch (error) {
      console.error("Error fetching task list:", error);
      toast.error("Failed to fetch tasks", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      return [];
    }
  }

  // Populate tasks from edit data
  function populateAttachedTasks(data) {
    try {
      if (!data.task_id_list || !data.task_list) {
        console.log("No tasks to populate");
        return;
      }

      // Parse task IDs and names
      const taskIds = data.task_id_list.split(",");
      const taskNames = data.task_list.split(",");

      // Create selected tasks array
      const selectedTasks = taskIds.map((id, index) => ({
        value: id.trim(),
        label: (taskNames[index] || id).trim()
      }));

      // Set form value if tasks exist
      if (selectedTasks.length > 0) {
        console.log("Setting sop_task value with", selectedTasks.length, "tasks:", selectedTasks);
        setValue("sop_task", selectedTasks);
      }
    } catch (error) {
      console.error("Error populating tasks:", error);
    }
  }

  // Generate form fields
  const GenerateForm = (fields, register, errors, control) => {
    const hasResourceField = resourceFields.length > 0;
    const selectedTaskCount = Array.isArray(selectedTask) ? selectedTask.length : 0;

    const sopTaskIndex = fields.findIndex((f) => f.name === "sop_task");
    const sopTaskField = sopTaskIndex >= 0 ? fields[sopTaskIndex] : null;
    // Fields before sop_task (incidentId, severity) — fills a complete 2-column row
    const topFields = sopTaskIndex >= 0 ? fields.slice(0, sopTaskIndex) : fields;
    // Fields after sop_task
    const afterTaskFields = sopTaskIndex >= 0 ? fields.slice(sopTaskIndex + 1) : [];
    // SOP Name goes beside sop_task (right column), description goes below
    const nameField = afterTaskFields.find((f) => f.name === "name") || null;
    const descriptionField = afterTaskFields.find((f) => f.name === "description") || null;
    const bottomFields = afterTaskFields.filter((f) => f.name !== "name" && f.name !== "description");

    return (
      <div>
        {/* Top fields: incidentId, task_type */}
        {topFields.length > 0 && (
          <FormHandler
            register={register}
            errors={errors}
            fields={topFields}
            control={control}
            columns={2}
            getValues={getValues}
          />
        )}

        {/* sop_task (left) + SOP Description (right) row */}
        {sopTaskField && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "0.9rem" }}>
            {/* LEFT: sop_task with custom label area */}
            <div style={{ paddingLeft: "1.2rem", paddingRight: "1.2rem" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  <label style={{ color: "black", marginBottom: 0, fontWeight: "500", fontSize: "12px" }}>
                    {t(sopTaskField.label)}
                    {sopTaskField.mandatory && <span className="asterisk"> *</span>}
                  </label>
                  {selectedTaskCount > 0 && (
                    <span style={{ background: "#1C4E72", color: "#fff", fontSize: "11px", fontWeight: "600", borderRadius: "20px", padding: "2px 12px", letterSpacing: "0.3px" }}>
                      {selectedTaskCount} {selectedTaskCount === 1 ? "task" : "tasks"} selected
                    </span>
                  )}
                  {selectedTaskCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setValue("sop_task", [])}
                      style={{ background: "#fff", color: "#c0392b", border: "1.5px solid #c0392b", borderRadius: "6px", padding: "3px 12px", fontSize: "11px", fontWeight: "600", cursor: "pointer", flexShrink: 0 }}
                      onMouseEnter={e => { e.currentTarget.style.background = "#c0392b"; e.currentTarget.style.color = "#fff"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#c0392b"; }}
                    >
                      Clear All
                    </button>
                  )}
                </div>
                <span onClick={navToCreateTask} style={{ color: "#ffa100", fontSize: "12px", cursor: "pointer", fontWeight: "500", flexShrink: 0 }}>
                  {t("addtask")}
                </span>
              </div>
              <MultiSelectWrapper
                name="sop_task"
                label=""
                errors={errors}
                options={sopTaskField.options}
                control={control}
                disabled={sopTaskField.disabled}
                mandatory={false}
              />
            </div>
            {/* RIGHT: SOP Name */}
            {nameField && (
              <FormHandler
                register={register}
                errors={errors}
                fields={[nameField]}
                control={control}
                columns={1}
                getValues={getValues}
              />
            )}
          </div>
        )}

        {/* SOP Description — full width */}
        {descriptionField && (
          <FormHandler
            register={register}
            errors={errors}
            fields={[descriptionField]}
            control={control}
            columns={1}
            getValues={getValues}
          />
        )}

        {/* Bottom fields: sop_resource, name, description, ... */}
        {bottomFields.length > 0 && (
          <FormHandler
            register={register}
            errors={errors}
            fields={bottomFields}
            control={control}
            columns={2}
            getValues={getValues}
          />
        )}

        {/* Selected Resources for SOP — collapsible */}
        {hasResourceField && (
          <div className="mt-2 px-3 pb-3">
            <div style={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #000" }}>
              <table className="table table-sm mb-0" style={{ borderCollapse: "collapse" }}>
                <thead>
                  <tr
                    onClick={() => setIsSelectedResourcesExpanded(prev => !prev)}
                    style={{ cursor: "pointer" }}
                  >
                    <th className="ps-2 py-2" style={{ backgroundColor: "#1C4E72", border: "none", color: "#fff", fontWeight: "700", fontStyle: "normal" }}>
                      {t("sopformlabels.selectedresources") || "Selected Resources for SOP"}
                      <span style={{ marginLeft: "10px", background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: "11px", fontWeight: "600", borderRadius: "20px", padding: "2px 10px", border: "1px solid rgba(255,255,255,0.3)" }}>
                        {resourceFields.length} {resourceFields.length === 1 ? "Resource" : "Resources"}
                      </span>
                    </th>
                    <th className="text-center py-2" style={{ backgroundColor: "#1C4E72", border: "none", color: "#fff", fontWeight: "700", fontStyle: "normal", width: "100px" }}>{"Available Qty"}</th>
                    <th className="text-center py-2" style={{ backgroundColor: "#1C4E72", border: "none", color: "#fff", fontWeight: "700", fontStyle: "normal", width: "100px" }}>{"Damaged Qty"}</th>
                    <th className="text-center py-2" style={{ backgroundColor: "#1C4E72", border: "none", color: "#fff", fontWeight: "700", fontStyle: "normal", width: "100px" }}>{"Deployed Qty"}</th>
                    <th className="text-center py-2" style={{ backgroundColor: "#1C4E72", border: "none", color: "#fff", fontWeight: "700", fontStyle: "normal", width: "100px" }}>{"Total Qty"}</th>
                    <th className="text-center py-2" style={{ backgroundColor: "#1C4E72", border: "none", color: "#fff", fontWeight: "700", fontStyle: "normal", width: "130px" }}>{"Qty To Deploy"}</th>
                    <th className="text-center py-2" style={{ backgroundColor: "#1C4E72", border: "none", color: "#fff", fontWeight: "700", fontStyle: "normal", width: "80px" }}>{"Auto"}</th>
                    <th className="text-center py-2" style={{ backgroundColor: "#1C4E72", border: "none", color: "#fff", fontWeight: "700", fontStyle: "normal", width: "80px" }}>
                      <span style={{ marginRight: "6px" }}>{"Manual"}</span>
                      <i className={`fas fa-chevron-${isSelectedResourcesExpanded ? "up" : "down"} sop-chevron-icon`} style={{ fontSize: "12px" }}></i>
                    </th>
                  </tr>
                </thead>
                {isSelectedResourcesExpanded && (
                  <tbody>
                    {resourceFields.map((field, index) => (
                      <tr key={field.id} className="align-middle"
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = "#f4fbfb"}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = ""}
                      >
                        <td className="ps-2 py-1" style={{ borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: "1px solid #dee2e6" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontWeight: "400", fontSize: "11px", color: "#1a2e35", minWidth: "16px" }}>
                              {index + 1}.
                            </span>
                            <span style={{ fontWeight: "400", fontSize: "11px", color: "#1a2e35" }}>{field.name}</span>
                          </div>
                        </td>
                        <td className="text-center py-1" style={{ borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: "1px solid #dee2e6", fontSize: "11px", color: "#1a2e35" }}>
                          {field.cum_available_quantity ?? 0}
                        </td>
                        <td className="text-center py-1" style={{ borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: "1px solid #dee2e6", fontSize: "11px", color: "#1a2e35" }}>
                          {field.cum_damaged_quantity ?? 0}
                        </td>
                        <td className="text-center py-1" style={{ borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: "1px solid #dee2e6", fontSize: "11px", color: "#1a2e35" }}>
                          {field.cum_deployed_quantity ?? 0}
                        </td>
                        <td className="text-center py-1" style={{ borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: "1px solid #dee2e6", fontSize: "11px", color: "#1a2e35" }}>
                          {field.cum_total_quantity ?? 0}
                        </td>
                        <td className="text-center py-1" style={{ borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: "1px solid #dee2e6" }}>
                          <input
                            type="number"
                            readOnly
                            className="form-control form-control-sm mx-auto"
                            style={{ width: "80px", textAlign: "center", fontSize: "11px", border: "1.5px solid #18a3a0", borderRadius: "8px" }}
                            {...register(`resource_items.${index}.quantity`)}
                            defaultValue={field.quantity}
                          />
                        </td>
                        <td className="text-center py-1" style={{ borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: "1px solid #dee2e6" }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            style={{ width: "15px", height: "15px", accentColor: "#18a3a0" }}
                            {...register(`resource_items.${index}.is_automatic`)}
                          />
                        </td>
                        <td className="text-center py-1" style={{ borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: "1px solid #dee2e6" }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            style={{ width: "15px", height: "15px", accentColor: "#18a3a0" }}
                            {...register(`resource_items.${index}.is_manual`)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Generate tab data
  const generateTabsData = (tabWiseData) => {
    return tabWiseData.map(element => ({
      ...element,
      component: GenerateForm(
        element.fields,
        register,
        errors,
        control
      )
    }));
  };

  // Navigation handlers
  const navToCreateTask = () => {
    navigate("../create-task");
  };

  const tabsData = generateTabsData(SoPConfig);

  // const navToCreateResource = () => {
  //   navigate("../../resource-list/create-resource");
  // };

  // Form submission handler
  const submitHandler = async (data, e) => {
    e.preventDefault();
    setIsDisabled(true);

    try {
      data.status = true;
      data.task_type = "0"; // Always Activity

      // Process tasks
      const sop_task_map = data.sop_task?.map(task => ({
        task_id: task.value,
        ...(edit_data ? { sop_id: data.id } : {})
      })) || [];

      data.sop_task_map = sop_task_map;

      // Process resources based on resource_items field array
      const sop_resource_instance_map = data.resource_items?.map(res => ({
        resource_id: parseInt(res.resource_id),
        severityId: parseInt(res.severityId),
        quantity: parseInt(res.quantity),
        isAutomatic: res.is_automatic === true || res.is_automatic === 1 || res.is_automatic === "true",
        isManual: res.is_manual === true || res.is_manual === 1 || res.is_manual === "true",
        status: 483, // Configuration status code
        ...(edit_data ? { sop_id: data.id } : {})
      })) || [];

      data.sop_resource_instance_map = sop_resource_instance_map;
      data.state_id = stateId;

      // Ensure incidentId is included (mapping from the first field)
      if (data.incidentId && typeof data.incidentId === "object") {
        data.incidentId = data.incidentId.value;
      }

      // Log the final form data being submitted
      console.log("Submitting form data:", data);

      // Submit data
      let res;
      if (edit_data) {
        res = await putDataWithBody(updateMasterSOPURL, data, {});
      } else {
        res = await postDataWithBody(createMasterSOPURL, data, {});
      }

      // Handle response
      if (res.status === "success") {
        toast.success(res.msg, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        navigate("/planning/view-sop");
      } else {
        toast.error(res.msg, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("An error occurred while saving the SOP", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsDisabled(false);
    }
  };

  const resourceItemsForValidation = watch("resource_items") || [];
  const areResourcesConfigured = resourceItemsForValidation.length === 0 ||
    resourceItemsForValidation.every(res => res.is_automatic || res.is_manual);

  return (
    <div className="mt-4 facility-container">
      <div className="form-body">
        <div className="registration__form--body">
          {/* <div
            onClick={navToCreateResource}
            className={`add_resource_sop_mgmnt ${collapsed ? "" : "expanded"}`}
          >
            {t("addresource")}
          </div> */}
          <form onSubmit={handleSubmit(submitHandler)}>
            <div>
              {tabsData?.map((tab) => (
                <React.Fragment key={tab.id}>
                  {tab.id === activeTab && (
                    <div>{tab.component}</div>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="d-flex justify-content-center">
              {tabsData.length - 1 === activeTab && (
                <Button
                  type="submit"
                  className="mx-2"
                  variant="primary"
                  disabled={isDisabled || !areResourcesConfigured}
                >
                  {edit_data ? t("Update") : t("saveAndSubmit")}
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default SOPForm;

